import { useTranslation } from '@helios/frontend-core';
import { hide, showMessages, startConversation } from '@intercom/messenger-js-sdk';
import { useCallback, useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { SunnyChatMode } from './SunnyChat';
import { getSunnyChatRecommendedQuestionsKey } from './sunnyChatConfig';

export interface UseSunnyChatResult {
  shouldRender: boolean;
  mode: SunnyChatMode;
  questions: string[];
  hasUnread: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onSelectQuestion: (question: string) => void;
  onSubmit: (message: string) => void;
  onLauncherClick: () => void;
}

export function useSunnyChat(route: string | undefined, enabled: boolean): UseSunnyChatResult {
  const { t } = useTranslation();
  const { hasActiveChat, hasActiveSunnyChat, unreadCount, setActiveChat, setActiveSunnyChat, isMessengerOpen } =
    useChat();
  const [isExpanded, setIsExpanded] = useState(false);

  const recommendedQuestionsKey = getSunnyChatRecommendedQuestionsKey(route);
  const translatedQuestions = recommendedQuestionsKey ? t(recommendedQuestionsKey, { returnObjects: true }) : [];
  const questions = Array.isArray(translatedQuestions) ? translatedQuestions : [];

  const onExpand = useCallback(() => setIsExpanded(true), []);
  const onCollapse = useCallback(() => setIsExpanded(false), []);

  const askQuestion = useCallback(
    (message: string) => {
      if (typeof window !== 'undefined') {
        startConversation(message);
      }
      setActiveChat(true);
      setActiveSunnyChat(true);
      setIsExpanded(false);
    },
    [setActiveChat, setActiveSunnyChat],
  );

  const onLauncherClick = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (isMessengerOpen) {
      hide();
    } else {
      showMessages();
    }
  }, [isMessengerOpen]);

  const mode: SunnyChatMode = hasActiveChat ? 'launcher' : isExpanded ? 'expanded' : 'bar';

  return {
    shouldRender: enabled && (Boolean(recommendedQuestionsKey) || hasActiveSunnyChat),
    mode,
    questions,
    hasUnread: unreadCount > 0,
    onExpand,
    onCollapse,
    onSelectQuestion: askQuestion,
    onSubmit: askQuestion,
    onLauncherClick,
  };
}
